import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGoogleLogin } from '@react-oauth/google'
import { Turnstile } from '@marsidev/react-turnstile'
import Modal from '@/v3-dashboard/components/UI/Modal'
import Button from '@/shared/components/UI/Button'
import Input from '@/shared/components/UI/Input'
import Checkbox from '@/shared/components/UI/Checkbox'
import GoogleIcon from '@/shared/icons/GoogleIcon'
import CircledCheck from '@/shared/icons/CircledCheck'
import signupGoogleUseCase from '@/application/auth/signupGoogle'
import IconWrapper from '@/shared/components/UI/IconWrapper'
import { sendMagicLink } from '@/services/onboarding.service'
import { useUserStore } from '@/stores/user/userStore'
import mixpanel from '@/utils/mixpanel'

const features = [
  'Train on your own data (Upload PDFs, Docx, or website URLs)',
  'Full AI Chatbot Customization',
  'Publish & embed your chatbot on your site',
  'Start for free (No credit card required)',
]

const isCaptchaEnabled = () => {
  const siteKey = import.meta.env.VITE_APP_TURNSTILE_SITEKEY
  return siteKey && siteKey !== 'YOUR_SITE_KEY_HERE' && siteKey !== 'DISABLED'
}

const DModalDemoUpgrade = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [agreeTerms, setAgreeTerms] = useState(true)
  const [agreeDiscounts, setAgreeDiscounts] = useState(true)
  const [emailSent, setEmailSent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaError, setCaptchaError] = useState('')
  const captchaRef = useRef(null)

  const agreeDiscountsRef = useRef(agreeDiscounts)
  agreeDiscountsRef.current = agreeDiscounts
  const captchaTokenRef = useRef('')
  captchaTokenRef.current = captchaToken
  const googleFlowStartRef = useRef(null)

  const resetCaptcha = () => {
    if (isCaptchaEnabled() && captchaRef.current) {
      try {
        captchaRef.current.reset()
      } catch {
        /* ignore */
      }
    }
    setCaptchaToken('')
    setCaptchaError('')
  }

  const isCaptchaSuccessful = () => !isCaptchaEnabled() || !!captchaToken

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('')
      setEmailError('')
      setError('')
      setEmailSent(false)
      setLoading(false)
      if (isCaptchaEnabled() && captchaRef.current) {
        try {
          captchaRef.current.reset()
        } catch {
          /* ignore */
        }
      }
      setCaptchaToken('')
      setCaptchaError('')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    mixpanel.track('Demo Upgrade Modal Viewed', {
      page: 'demo_upgrade_modal',
      captcha_enabled: isCaptchaEnabled(),
    })
  }, [isOpen])

  const handleGoogleSignUp = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const demoUserIdBeforeSignup = useUserStore.getState().user?.id
      setLoading(true)
      setError('')
      try {
        const captcha_token = isCaptchaEnabled() ? captchaTokenRef.current || '' : undefined
        const response = await signupGoogleUseCase({
          ...tokenResponse,
          rewardful_referral: window.Rewardful?.referral,
          contact_consent: agreeDiscountsRef.current,
          from_demo_account: true,
          demo_source_user_id: demoUserIdBeforeSignup,
          ...(captcha_token !== undefined ? { captcha_token } : {}),
        })
        if (response.status === 200) {
          mixpanel.track('Google Sign Up Succeeded', {
            page: 'demo_upgrade_modal',
            method: 'google',
            marketing_consent: agreeDiscountsRef.current,
            time_to_complete_ms:
              googleFlowStartRef.current != null
                ? Date.now() - googleFlowStartRef.current
                : undefined,
          })
          onClose()
          navigate('/first-time-setup')
        } else {
          setError('Google sign up failed. Please try again.')
          mixpanel.track('Google Sign Up Failed', {
            page: 'demo_upgrade_modal',
            method: 'google',
            error_type: 'backend_error',
            error_message: 'Unexpected response from signup',
          })
        }
      } catch (err) {
        const errorDetail = err.response?.data?.detail || ''
        if (
          err.response?.status === 400 &&
          (errorDetail.toLowerCase().includes('captcha') ||
            errorDetail.toLowerCase().includes('invalid captcha') ||
            errorDetail.toLowerCase().includes('captcha verification failed'))
        ) {
          setCaptchaError('CAPTCHA verification failed. Please try again.')
          setError('')
          resetCaptcha()
          mixpanel.track('Google Sign Up Failed', {
            page: 'demo_upgrade_modal',
            method: 'google',
            error_type: 'captcha_failed',
            error_message: errorDetail || 'CAPTCHA verification failed',
          })
        } else {
          setError(err.response?.data?.detail || err.message || 'Google sign up failed')
          mixpanel.track('Google Sign Up Failed', {
            page: 'demo_upgrade_modal',
            method: 'google',
            error_type: err.response?.status ? 'backend_error' : 'oauth_error',
            error_message: err.response?.data?.detail || err.message || 'Google sign up failed',
          })
        }
      } finally {
        setLoading(false)
      }
    },
    onError: () => {
      setError('Google sign up failed. Please try again.')
      mixpanel.track('Google Sign Up Failed', {
        page: 'demo_upgrade_modal',
        method: 'google',
        error_type: 'oauth_error',
        error_message: 'Google sign up failed. Please try again.',
      })
      setLoading(false)
    },
  })

  const handleGoogleButtonClick = () => {
    if (!isCaptchaSuccessful()) {
      setCaptchaError('Please complete the CAPTCHA verification')
      return
    }
    setCaptchaError('')
    googleFlowStartRef.current = Date.now()
    mixpanel.track('Google Sign Up Initiated', {
      page: 'demo_upgrade_modal',
      method: 'google',
      marketing_consent: agreeDiscounts,
    })
    handleGoogleSignUp()
  }

  const handleEmailContinue = async () => {
    if (!email.trim()) {
      setEmailError('Email is required')
      return
    }
    if (!isCaptchaSuccessful()) {
      setCaptchaError('Please complete the CAPTCHA verification')
      return
    }
    setLoading(true)
    setError('')
    setCaptchaError('')
    const isResend = emailSent
    const emailDomain = email.trim().split('@')[1] || ''
    mixpanel.track('Signup Email Submitted', {
      page: 'demo_upgrade_modal',
      email_domain: emailDomain,
      marketing_consent: agreeDiscounts,
      is_resend: isResend,
    })
    try {
      const captcha_token = isCaptchaEnabled() ? captchaToken || '' : undefined
      const demoSourceUserId = useUserStore.getState().user?.id
      await sendMagicLink(email, agreeDiscounts, null, {
        from_demo_account: true,
        ...(demoSourceUserId ? { demo_source_user_id: demoSourceUserId } : {}),
        ...(captcha_token !== undefined ? { captcha_token } : {}),
      })
      setEmailSent(true)
      mixpanel.track('Signup Email Sent', { page: 'demo_upgrade_modal' })
    } catch (err) {
      const errorDetail = err.response?.data?.detail || ''
      if (
        err.response?.status === 400 &&
        (errorDetail.toLowerCase().includes('captcha') ||
          errorDetail.toLowerCase().includes('invalid captcha') ||
          errorDetail.toLowerCase().includes('captcha verification failed'))
      ) {
        setCaptchaError('CAPTCHA verification failed. Please try again.')
        setError('')
        resetCaptcha()
        mixpanel.track('Signup Email Failed', {
          page: 'demo_upgrade_modal',
          error_type: 'captcha_failed',
          error_message: errorDetail,
        })
      } else {
        setError(errorDetail || err.message || 'Failed to send verification link')
        mixpanel.track('Signup Email Failed', {
          page: 'demo_upgrade_modal',
          error_type: 'server_error',
          error_message: errorDetail || err.message || 'Failed to send verification link',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Build your own custom AI"
      description="Create a chatbot trained on your data in minutes"
      className="md:max-w-[500px] md:w-auto shadow-v3-modal !pb-v3-lg"
      titleClassName="font-['Lora'] italic"
      headerClassName="!border-b-0 !pb-v3-sm"
    >
      {/* Feature checklist */}
      <div className="flex flex-col gap-v3-sm mb-v3-md">
        {features.map((feature) => (
          <div key={feature} className="flex items-center gap-v3-xs">
            <CircledCheck className="w-4 h-4 text-v3-green-dark flex-shrink-0" />
            <span className="text-v3-h10 text-v3-black-max">{feature}</span>
          </div>
        ))}
      </div>

      {isCaptchaEnabled() && (
        <div className="mb-v3-md">
          <Turnstile
            ref={captchaRef}
            siteKey={import.meta.env.VITE_APP_TURNSTILE_SITEKEY}
            options={{
              action: 'signup',
              cData: 'demo-upgrade-modal',
              theme: 'light',
              size: 'flexible',
            }}
            onSuccess={(token) => {
              setCaptchaToken(token)
              setCaptchaError('')
            }}
            onError={() => {
              setCaptchaError('CAPTCHA verification failed. Please try again.')
              setCaptchaToken('')
            }}
            onExpire={() => {
              setCaptchaToken('')
              setCaptchaError('CAPTCHA expired. Please verify again.')
            }}
          />
          {captchaError && (
            <p className="text-red-500 text-xs mt-2">{captchaError}</p>
          )}
        </div>
      )}

      {/* Google button */}
      <Button
        icon={GoogleIcon}
        label="Continue with Google"
        onClick={handleGoogleButtonClick}
        disabled={loading || !isCaptchaSuccessful()}
        size="lg"
        className="w-full justify-center !rounded-[24px] !bg-white !border-v3-foreground-2 !text-v3-black-max hover:!bg-v3-bg-lighter"
        iconColor="text-v3-black-max"
      />

      {/* OR divider */}
      <div className="flex items-center gap-v3-xs pt-v3-sm pb-v3-ms">
        <div className="flex-1 border-t border-v3-black-inactive" />
        <span className="text-v3-h9 text-v3-black-max">OR</span>
        <div className="flex-1 border-t border-v3-black-inactive" />
      </div>

      {/* Email input + submit */}
      <div className="flex flex-col gap-v3-md min-h-[250px]">
        {emailSent ? (
          <div className="flex flex-col items-center py-v3-md text-center">
            <IconWrapper size={40} className="text-v3-green-dark">
              <CircledCheck className="w-10 h-10" />
            </IconWrapper>
            <div className="flex flex-col gap-v3-xxs pb-v3-lg">
              <span className="text-v3-h8 text-v3-black-max">Check your inbox</span>
              <span className="text-v3-h9 text-v3-black-secondary">
                We sent a link to <strong className="text-v3-black-max">{email}</strong>
              </span>
            </div>
            <div className="flex gap-v3-sm">
              <Button
                label="Resend Code"
                variant="primary"
                size="sm"
                onClick={handleEmailContinue}
                loading={loading}
                disabled={loading || !isCaptchaSuccessful()}
              />
              <Button
                label="Change Email"
                variant="secondary"
                size="sm"
                onClick={() => setEmailSent(false)}
              />
            </div>
          </div>
        ) : (
          <>
            <Input
              label="Email"
              type="email"
              placeholder="Enter your work email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError('')
                if (error) setError('')
              }}
              helperText="Use this email to manage the Dante AI account"
              error={emailError || error}
              disabled={loading}
              onEnter={handleEmailContinue}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleEmailContinue()
                }
              }}
            />
            <Button
              label="Send Verification Link"
              variant="primary"
              size="lg"
              onClick={handleEmailContinue}
              className="w-full justify-center"
              disabled={loading || !agreeTerms || !isCaptchaSuccessful()}
              loading={loading}
            />
            {/* Consent checkboxes */}
      <div className="flex flex-col gap-v3-xs pt-v3-lg">
        <div className="flex items-start gap-v3-xs">
          <Checkbox checked={agreeTerms} onChange={setAgreeTerms} />
          <span
            className="text-v3-h9 text-v3-black-max cursor-pointer leading-[22px]"
            onClick={() => setAgreeTerms((v) => !v)}
          >
            I agree to{' '}
            <a
              href="https://www.dante-ai.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              onClick={(e) => e.stopPropagation()}
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              href="https://www.dante-ai.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </a>
          </span>
        </div>
        <Checkbox
          checked={agreeDiscounts}
          onChange={setAgreeDiscounts}
          label="I want to receive discounts, exclusive updates and AI tips"
        />
      </div>
          </>
          
        )}
      </div>

      
    </Modal>
  )
}

export default DModalDemoUpgrade
